import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import * as uuid from 'uuid';

import {
  Auth,
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  idToken,
  OAuthProvider,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as firebaseSignOut,
  UserCredential,
} from '@angular/fire/auth';
import { catchError, map, Observable, of, Subscription } from 'rxjs';
import { UserService } from '../user/user.service';
import { ActivatedRoute, Router } from '@angular/router';
import { WorkOfArtService } from '../work-of-art/work-of-art.service';
import { ArtistService } from '../artist/artist.service';
@Injectable({ providedIn: 'root' })
export class AuthService {
  private _auth = inject(Auth);
  private _httpClient = inject(HttpClient);
  private _userService = inject(UserService);

  private _workOfArtService = inject(WorkOfArtService);
  private _artistsService = inject(ArtistService);

  private _authenticated = false;
  private _backendUrl = environment.backendUrl;

  idToken$ = idToken(this._auth);
  idTokenSubscription: Subscription;

  constructor(
    private _activatedRoute: ActivatedRoute,
    private _router: Router
  ) {
    this.idTokenSubscription = this.idToken$.subscribe((token) => {
      if (token) {
        this.getJwt(token).subscribe({
          next: (response: any) => {
            // this._router.navigateByUrl('/');
          },
          error: (error) => {},
        });
      }
    });
  }

  get authenticated(): boolean {
    return this._authenticated;
  }
  set authenticated(value: boolean) {
    this._authenticated = value;
  }

  set accessToken(token: string) {
    localStorage.setItem('accessToken', token);
  }

  get accessToken(): string {
    return localStorage.getItem('accessToken') ?? '';
  }

  set refreshToken(token: string) {
    localStorage.setItem('refreshToken', token);
  }

  get refreshToken(): string {
    return localStorage.getItem('refreshToken') ?? '';
  }

  set code(code: string) {
    localStorage.setItem('code', code);
  }

  get code(): string {
    return localStorage.getItem('code') ?? '';
  }

  /** Google */
  loginWithGoogle(): Promise<UserCredential> {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    return signInWithPopup(this._auth, provider);
  }

  /** Apple */
  loginWithApple(): Promise<UserCredential> {
    const provider = new OAuthProvider('apple.com');
    provider.addScope('email');
    provider.addScope('name');
    provider.setCustomParameters({ locale: 'es' });
    return signInWithPopup(this._auth, provider);
  }

  /** Email + Password Sign In */
  signInWithEmail(email: string, password: string): Promise<UserCredential> {
    return signInWithEmailAndPassword(this._auth, email, password);
  }

  /** Email + Password Sign Up */
  signUpWithEmail(email: string, password: string): Promise<UserCredential> {
    return createUserWithEmailAndPassword(this._auth, email, password);
  }

  /** Sign Out */
  signOut(): void {
    firebaseSignOut(this._auth)
      .then(() => {
        this._authenticated = false;
        this._userService.user = null;
        this.accessToken = '';
        this.refreshToken = '';
        this.code = '';
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('code');
        this._workOfArtService.clearFavoriteWorksOfArt();
        this._artistsService.clearFollowingArtists();
      })
      .catch((err) => console.error('Sign-out error', err));
  }

  /** Password Reset */
  forgotPassword(email: string): Promise<void> {
    return sendPasswordResetEmail(this._auth, email);
  }

  getJwt(token: String): Observable<any> {
    this.code = uuid.v4();

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      iss: 'artstgo',
      sub: 'client-web',
      aud: 'sign-in',
      code: this.code,
    });

    return this._httpClient
      .post(
        this._backendUrl + 'client/auth/sign-in',
        {
          tokenFirebase: token,
        },
        { headers }
      )
      .pipe(
        map((response: any) => {
          this.accessToken = response.payload.accessToken;
          this.refreshToken = response.payload.refreshToken;

          this._authenticated = true;

          this._userService.user = response.payload.user;

          return response;
        }),
        catchError((error) => {
          return of(error.error);
        })
      );
  }

  check(): Observable<boolean> {
    // Check if the user is logged in
    if (this._authenticated) {
      return of(true);
    }
    return of(false);
  }
}
